import { createFileRoute, Link } from '@tanstack/react-router'
import { useRef, useEffect, useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip,
} from 'recharts'
import { SCHOOLS_REGISTRY } from '@cougargrades/models'
import { computeUDI, deriveUDIInputs } from '@cougargrades/utils'
import type { UDIResult } from '@cougargrades/utils'
import { API_ORIGIN } from '@cougargrades/services/environment'
import { useTrending } from '../lib/services/useTrending'
import { useStats } from '../lib/services/useStats'
import { useTopResults } from '../lib/services/useTopResults'
import { GpaBadge } from '../components/ui/GpaBadge'
import { UDIBadge } from '../components/ui/UDIBadge'
import { GradeBar } from '../components/ui/GradeBar'
import { Skeleton } from '../components/ui/Skeleton'
import { GpaPredictor } from '../components/GpaPredictor'
import { getSchoolTheme } from '../lib/schoolThemes'
import { GRADE_COLORS } from '../lib/gradeColors'
import type { SearchResult, TopResult } from '@cougargrades/models/dto'
import SearchBar from '../components/search'

interface CourseEnrichment {
  avgGPA: number
  gradeCounts: { A: number; B: number; C: number; D: number; F: number; W: number }
  sectionCount: number
}

// Same fetch pattern as search.tsx enrichment and GpaPredictor.tsx
async function fetchCourseEnrichments(ids: string[]): Promise<Record<string, CourseEnrichment | null>> {
  const results = await Promise.all(
    ids.map(async id => {
      try {
        const res = await fetch(`${API_ORIGIN.origin}/api/course/${encodeURIComponent(id)}`)
        if (!res.ok) return null
        const json = await res.json() as { badges: Array<{ key: string; text: string }>; dataGrid: { rows: Array<{ A: number; B: number; C: number; D: number; F: number; W: number }> }; sectionCount: number }
        const avgGPA = parseFloat(json.badges?.find(b => b.key === 'gpa')?.text ?? '0') || 0
        const gradeCounts = { A: 0, B: 0, C: 0, D: 0, F: 0, W: 0 }
        for (const row of (json.dataGrid?.rows ?? [])) {
          gradeCounts.A += row.A ?? 0
          gradeCounts.B += row.B ?? 0
          gradeCounts.C += row.C ?? 0
          gradeCounts.D += row.D ?? 0
          gradeCounts.F += row.F ?? 0
          gradeCounts.W += row.W ?? 0
        }
        return { avgGPA, gradeCounts, sectionCount: json.sectionCount ?? 0 }
      } catch (err) {
        console.error('[fetchCourseEnrichments] failed for', id, err)
        return null
      }
    })
  )
  const map: Record<string, CourseEnrichment | null> = {}
  ids.forEach((id, i) => { map[id] = results[i] })
  return map
}

// ─── CSS animation injection ──────────────────────────────────────────────────

const PAGE_STYLES = `
@keyframes ticker { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }
@keyframes bounce-y { 0%,100% { transform: translateY(0) } 50% { transform: translateY(8px) } }
@keyframes fade-up { from { opacity: 0; transform: translateY(18px) } to { opacity: 1; transform: translateY(0) } }
@keyframes pulse-dot { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }
.ticker-track { animation: ticker 50s linear infinite; white-space: nowrap; display: inline-flex; gap: 48px }
.ticker-track:hover { animation-play-state: paused }
.bounce-arrow { animation: bounce-y 1.4s ease-in-out infinite }
.pulse-dot { animation: pulse-dot 1.5s ease-in-out infinite }
.section-enter { opacity: 0; transform: translateY(18px); transition: opacity 0.6s ease-out, transform 0.6s ease-out }
.section-enter.visible { opacity: 1; transform: translateY(0) }

/* ── Mobile responsive overrides (≤ 640px) ── */
@media (max-width: 640px) {
  .hero-school-badge { display: none !important; }
  .dashboard-grid { grid-template-columns: 1fr !important; }
  .feature-row-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
  .trending-grid { grid-template-columns: 1fr !important; }
  .school-coverage-grid { grid-template-columns: 1fr 1fr !important; }
}
`

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/')({
  head: () => ({
    meta: [{ title: 'Unilytics — Real Grade Data for College Students' }],
  }),
  component: RouteComponent,
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractGpa(badges: SearchResult['badges']): number | null {
  const text = badges.find(b => b.key === 'gpa')?.text ?? ''
  const n = parseFloat(text)
  return n > 0 ? n : null
}


function extractDropRate(badges: SearchResult['badges']): number {
  const text = badges.find(b => b.key === 'droprate')?.text ?? ''
  return parseFloat(text) || 0
}

// ─── useInView hook ───────────────────────────────────────────────────────────

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect() } },
      { threshold },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return { ref, visible }
}

// ─── Animated counter ────────────────────────────────────────────────────────

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [value, setValue] = useState(0)
  const { ref, visible } = useInView(0.5)
  useEffect(() => {
    if (!visible) return
    const duration = 2000
    const start = performance.now()
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * target))
      if (progress < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }, [visible, target])
  return (
    <span ref={ref} style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 700 }}>
      {value.toLocaleString()}{suffix}
    </span>
  )
}

// ─── SectionWrapper (fade-up on scroll) ──────────────────────────────────────

function SectionWrapper({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const { ref, visible } = useInView()
  return (
    <div ref={ref} className={`section-enter${visible ? ' visible' : ''}`} style={style}>
      {children}
    </div>
  )
}

// ─── Main route component ─────────────────────────────────────────────────────

function RouteComponent() {
  // Existing data hooks — preserved exactly
  const { data: trending, isPending: trendingPending } = useTrending(20)
  const { data: stats } = useStats()
  const trendingEmpty = !trendingPending && (!trending || trending.length === 0)
  const { data: topCourses, isPending: topPending } = useTopResults({
    metric: 'totalEnrolled',
    topic: 'course',
    limit: 20,
    skip: 0,
    time: 'all',
    hideCore: false,
  })

  // Apply school theme CSS vars on mount
  useEffect(() => {
    const theme = getSchoolTheme('uh')
    document.documentElement.style.setProperty('--school-primary', theme.primaryColor)
    document.documentElement.style.setProperty('--school-secondary', theme.secondaryColor)
  }, [])

  const totalRecords = stats?.totalRecords ?? 133000
  const totalCourses = stats?.totalCourses ?? 7600
  const totalProfessors = stats?.totalProfessors ?? 6200
  const theme = getSchoolTheme('uh')

  const trendingCourses = (trending ?? []).filter(r => r.type === 'course')

  return (
    <div style={{ fontFamily: 'var(--font-family-sans)' }}>
      <style>{PAGE_STYLES}</style>

      {/* ── HERO ── */}
      <HeroSection
        totalRecords={totalRecords}
        totalCourses={totalCourses}
        totalProfessors={totalProfessors}
        theme={theme}
      />

      {/* ── TICKER ── */}
      <StaticTickerSection />

      {/* ── DASHBOARD ── */}
      <SectionWrapper>
        <DashboardSection trending={trendingCourses} isPending={trendingPending} />
      </SectionWrapper>

      {/* ── FEATURE ROWS ── */}
      <SectionWrapper>
        <FeatureRowsSection />
      </SectionWrapper>

      {/* ── TRUST BAR ── */}
      <SectionWrapper>
        <TrustBar totalRecords={totalRecords} />
      </SectionWrapper>

      {/* ── TRENDING COURSES ── */}
      <SectionWrapper>
        <TrendingSection
          trending={trending}
          trendingPending={trendingPending}
          trendingEmpty={trendingEmpty}
          topCourses={topCourses}
          topPending={topPending}
        />
      </SectionWrapper>

      {/* ── SCHOOL COVERAGE ── */}
      <SectionWrapper>
        <SchoolCoverageSection stats={stats} />
      </SectionWrapper>
    </div>
  )
}

// ─── HERO SECTION ─────────────────────────────────────────────────────────────

interface HeroProps {
  totalRecords: number
  totalCourses: number
  totalProfessors: number
  theme: ReturnType<typeof getSchoolTheme>
}

function HeroSection({ totalRecords, totalCourses, totalProfessors, theme }: HeroProps) {
  const scrollToNext = () => {
    window.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' })
  }

  return (
    <section
      style={{
        background: `linear-gradient(160deg, ${theme.primaryColor} 0%, #8B0A1A 100%)`,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px 60px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Grid pattern overlay */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      {/* School badge — top left */}
      <div className="hero-school-badge" style={{ position: 'absolute', top: 24, left: 24, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: theme.secondaryColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 900, fontSize: 12, color: theme.primaryColor,
          fontFamily: 'var(--font-family-mono)',
        }}>
          {theme.shortName}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.9)', lineHeight: 1.3 }}>
            {theme.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
            <span className="pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ADE80', display: 'inline-block' }} />
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>Live Data</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div style={{ textAlign: 'center', maxWidth: 720, position: 'relative', zIndex: 1 }}>
        {/* Eyebrow */}
        <p style={{
          fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.65)', marginBottom: 20, fontWeight: 600,
        }}>
          University of Houston · 133,000+ Grade Records
        </p>

        {/* H1 */}
        <h1 style={{
          fontSize: 'clamp(48px, 8vw, 80px)',
          fontWeight: 900, color: '#fff',
          lineHeight: 1.05, letterSpacing: '-0.03em',
          marginBottom: 24,
        }}>
          Know before you enroll
          <span style={{ color: theme.secondaryColor }}>.</span>
        </h1>

        {/* Subheadline */}
        <p style={{
          fontSize: 'clamp(16px, 2vw, 20px)',
          color: 'rgba(255,255,255,0.82)',
          maxWidth: 580, margin: '0 auto 36px',
          lineHeight: 1.65,
        }}>
          Real grade distributions for every course at UH.
          Find the right professor. Predict your GPA. Graduate smarter.
        </p>

        {/* Search bar — typeahead */}
        <div style={{ maxWidth: 600, margin: '0 auto 28px' }}>
          <SearchBar />
        </div>

        {/* Animated stat chips */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
          {[
            { value: totalRecords, suffix: '+', label: 'grade records' },
            { value: totalCourses, suffix: '+', label: 'courses' },
            { value: totalProfessors, suffix: '+', label: 'professors' },
          ].map(({ value, suffix, label }) => (
            <div key={label} style={{
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 999, padding: '7px 18px',
              fontSize: 13, color: 'rgba(255,255,255,0.75)',
            }}>
              <AnimatedCounter target={value} suffix={suffix} />{' '}{label}
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToNext}
        style={{
          position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        }}
      >
        <span className="bounce-arrow" style={{ fontSize: 22, color: 'rgba(255,255,255,0.5)' }}>↓</span>
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Explore UH insights
        </span>
      </button>
    </section>
  )
}

// ─── DASHBOARD SECTION ────────────────────────────────────────────────────────

function DashboardSection({ trending, isPending }: { trending: SearchResult[]; isPending: boolean }) {

  // Candidate pool: top 100 high-enrollment courses from /api/top — university-wide, not just trending
  // TODO: Remove hardcoded courses once Unilytics has its own API with full catalog UDI pre-computation
  const { data: topCoursePool, isLoading: topPoolLoading, isError: topPoolError } = useTopResults({
    metric: 'totalEnrolled', topic: 'course', limit: 100, skip: 0, time: 'all', hideCore: false,
  })

  // Fall back to trending if top API fails or returns nothing — avoids stuck skeleton
  const hardestCandidates = useMemo(() => {
    if (topPoolLoading) return [] // still waiting — keep empty, skeleton will show
    const useTopPool = !topPoolError && (topCoursePool?.length ?? 0) > 0
    const pool = useTopPool ? topCoursePool! : trending.filter((r): r is SearchResult => r.type === 'course')
    return pool
      .filter(r => r.href.startsWith('/c/'))
      .map(r => ({ ...r, dropRate: extractDropRate(r.badges), gpa: extractGpa(r.badges) }))
  }, [topCoursePool, topPoolLoading, topPoolError, trending])

  const hardestCourseIds = useMemo(
    () => hardestCandidates.map(c => decodeURIComponent(c.href.slice(3))),
    [hardestCandidates],
  )
  const { data: dashboardSummaries, isLoading: dashboardEnrichmentLoading } = useQuery({
    queryKey: ['course-enrichment-dashboard', hardestCourseIds],
    queryFn: () => fetchCourseEnrichments(hardestCourseIds),
    enabled: hardestCourseIds.length > 0,
    staleTime: 1000 * 60 * 5,
  })

  // Post-enrichment: filter by relevance, exclude grad courses, sort by UDI descending
  type EnrichedCandidate = (typeof hardestCandidates)[0] & { udiResult: UDIResult; totalEnrollment: number }
  const displayedHardestCourses = useMemo((): EnrichedCandidate[] => {
    if (!dashboardSummaries) return []

    const isGradCourse = (title: string) => {
      const match = title.match(/[A-Z]+\s+(\d)/)
      return match ? ['6','7','8','9'].includes(match[1]) : false
    }

    return hardestCandidates
      .flatMap(c => {
        const courseId = decodeURIComponent(c.href.slice(3))
        const e = dashboardSummaries[courseId]
        if (!e) return []
        const totalEnrollment = Object.values(e.gradeCounts).reduce((a, b) => a + b, 0)
        if ((totalEnrollment < 500 && e.sectionCount < 10) || isGradCourse(c.title)) return []
        const udiResult = computeUDI(deriveUDIInputs({ avgGPA: e.avgGPA, grades: e.gradeCounts, sections: e.sectionCount, schoolId: 'uh' }))
        return [{ ...c, udiResult, totalEnrollment }]
      })
      .sort((a, b) => b.udiResult.udi - a.udiResult.udi)
      .slice(0, 5)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hardestCandidates, dashboardSummaries])

  // TODO: Replace with /api/stats endpoint returning university-wide grade breakdown
  const gradeDistData = [
    { grade: 'A', pct: 36 }, { grade: 'B', pct: 28 }, { grade: 'C', pct: 18 },
    { grade: 'D', pct: 7 }, { grade: 'F', pct: 6 }, { grade: 'W', pct: 5 },
  ]

  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 16,
    border: '1px solid rgba(13,27,42,0.07)',
    boxShadow: '0 2px 8px rgba(13,27,42,0.05)',
    padding: '20px 22px',
  }

  return (
    <section style={{ background: '#F8FAFC', padding: '64px 0' }}>
      <div className="max-w-6xl mx-auto px-4">
        <div style={{ marginBottom: 36, borderLeft: '4px solid var(--school-primary)', paddingLeft: 16 }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0D1B2A', margin: 0, letterSpacing: '-0.02em' }}>
            University of Houston — Grade Intelligence
          </h2>
          <p style={{ fontSize: 14, color: '#64748B', marginTop: 6 }}>Live data from public university records</p>
        </div>

        <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
          {/* Card 1 — University-wide grade distribution */}
          <div style={cardStyle}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--school-primary)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 4px' }}>Grade Overview</p>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0D1B2A', margin: '0 0 16px' }}>How UH Students Grade Overall</h3>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={gradeDistData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <XAxis dataKey="grade" tick={{ fontSize: 11, fontFamily: 'var(--font-family-mono)', fill: '#64748B' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                <Tooltip
                  formatter={(v) => [`${typeof v === 'number' ? v : ''}%`, 'Students']}
                  contentStyle={{ borderRadius: 8, fontSize: 12, border: '1px solid #E2E8F0' }}
                />
                <Bar dataKey="pct" radius={[4, 4, 0, 0]} isAnimationActive animationBegin={200}>
                  {gradeDistData.map(({ grade }) => (
                    <Cell key={grade} fill={GRADE_COLORS[grade as keyof typeof GRADE_COLORS] ?? '#CBD5E1'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p style={{ fontSize: 12, color: '#64748B', marginTop: 10, lineHeight: 1.5 }}>
              64% of UH students earn an A or B across all courses.
            </p>
          </div>

          {/* Card 2 — Hardest courses */}
          <div style={cardStyle}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--school-primary)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 4px' }}>Challenge Rankings</p>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0D1B2A', margin: '0 0 14px' }}>Most Challenging Courses</h3>
            {(topPoolLoading && hardestCandidates.length === 0) || (hardestCandidates.length > 0 && dashboardEnrichmentLoading)
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={28} />)}</div>
              : displayedHardestCourses.length === 0
                ? <p style={{ fontSize: 13, color: '#94A3B8', fontStyle: 'italic' }}>Not enough data to rank courses yet.</p>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {displayedHardestCourses.map((c, i) => {
                      const code = c.title.split(': ')[0]
                      const name = c.title.split(': ').slice(1).join(': ') || code
                      return (
                        <a key={c.key} href={c.href} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                          <span style={{ width: 22, fontSize: 18, fontWeight: 900, color: '#E2E8F0', fontFamily: 'var(--font-family-mono)', flexShrink: 0 }}>{i + 1}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                              <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--school-primary)', padding: '2px 7px', borderRadius: 4 }}>{code}</span>
                              {c.dropRate > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: '#EF4444', background: 'rgba(239,68,68,0.08)', padding: '2px 7px', borderRadius: 4 }}>{c.dropRate.toFixed(1)}% W</span>}
                            </div>
                            <div style={{ fontSize: 12, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                          </div>
                          <UDIBadge udi={c.udiResult.udi} />
                        </a>
                      )
                    })}
                  </div>
            }
          </div>

          {/* Card 3 — GPA Predictor */}
          <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column' }}>
            <GpaPredictor trending={trending} isPending={isPending} />
          </div>

          {/* Card 4 — Trending this semester */}
          <div style={cardStyle}>
            <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--school-primary)', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 4px' }}>Live Activity</p>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0D1B2A', margin: '0 0 4px' }}>Trending This Semester</h3>
            <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 14 }}>Most searched courses right now</p>
            {trending.length === 0
              ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={32} />)}</div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {trending.slice(0, 4).map((item, i) => {
                    const [code, ...rest] = item.title.split(': ')
                    const name = rest.join(': ')
                    const gpa = extractGpa(item.badges)
                    return (
                      <a key={item.key} href={item.href} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', padding: '6px 0', borderBottom: i < 3 ? '1px solid rgba(13,27,42,0.05)' : 'none' }}>
                        <span style={{ fontSize: 16, flexShrink: 0, width: 24, textAlign: 'center' }}>{i === 0 ? '🔥' : `${i + 1}`}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 700, color: 'var(--school-primary)' }}>{code}</span>
                          {name && <span style={{ fontSize: 11, color: '#64748B', marginLeft: 6 }}>{name.length > 28 ? name.slice(0, 28) + '…' : name}</span>}
                        </div>
                        <GpaBadge gpa={gpa} size="sm" />
                      </a>
                    )
                  })}
                </div>
            }
            <Link to="/top" style={{ fontSize: 12, color: 'var(--school-primary)', display: 'block', marginTop: 12, textDecoration: 'none', fontWeight: 600 }}>
              View all trending →
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── STATIC TICKER SECTION ───────────────────────────────────────────────────
// TODO: replace static strings with live data from /api/stats or trending endpoint

const TICKER_ITEMS = [
  '📊 CHEM 2323 · 20.3% withdrawal rate · 2.15 avg GPA',
  '⭐ ENGL 1301 · 3.03 avg GPA · 869 sections taught',
  '🔥 COSC 1430 · Most searched CS course at UH',
  '📉 MATH 2413 · 2.44 avg GPA · Trending harder',
  '⚠️ BCHS 3304 · 14.2% withdrawal rate',
  '✅ KINE 1304 · 3.81 avg GPA · Easy difficulty',
  '📊 BIOL 4374 · 2.71 avg GPA · Moderate difficulty',
  '🎓 133,000+ grade records · 7,600+ courses at UH',
]

// Duplicated once for seamless infinite loop
const TICKER_ALL = [...TICKER_ITEMS, ...TICKER_ITEMS]

function StaticTickerSection() {
  return (
    <div style={{ background: 'linear-gradient(to right, var(--school-primary), #0D1B2A)', height: 44, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
      <div className="ticker-track">
        {TICKER_ALL.map((text, i) => (
          <span key={i} style={{ fontSize: 13, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}>
            {text}
            <span style={{ color: 'var(--school-secondary)', fontWeight: 700, margin: '0 24px' }}>·</span>
          </span>
        ))}
      </div>
    </div>
  )
}

// ─── FEATURE ROWS SECTION ─────────────────────────────────────────────────────

const MOCK_CHEM_GRADES = { A: 820, B: 640, C: 380, D: 150, F: 210, W: 310 }
const MOCK_PROF_A = { name: 'Dr. Martinez', gpa: 3.41 }
const MOCK_PROF_B = { name: 'Dr. Chen', gpa: 2.87 }

function FeatureRowsSection() {
  return (
    <section style={{ padding: '80px 0', background: '#fff' }}>
      <div className="max-w-6xl mx-auto px-4" style={{ display: 'flex', flexDirection: 'column', gap: 80 }}>
        <FeatureRow
          visualLeft
          eyebrow="Grade Intelligence"
          heading="See exactly how students performed."
          body="Every section. Every professor. Every semester. Real grade distributions from official university records — not opinions."
          cta={{ label: 'Explore courses →', href: '/search?q=' }}
          visual={
            <div style={{ background: '#F8FAFC', borderRadius: 16, padding: 20, border: '1px solid rgba(13,27,42,0.07)' }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 8 }}>CHEM 2323 — All Sections</p>
              <GradeBar grades={MOCK_CHEM_GRADES} height={14} showLabels />
              <p style={{ fontSize: 11, color: '#94A3B8', marginTop: 10 }}>2,510 students across 42 sections</p>
            </div>
          }
        />

        <FeatureRow
          visualLeft={false}
          eyebrow="Professor Intelligence"
          heading="Pick the professor who helps you succeed."
          body="Compare grade outcomes across every professor who teaches your course. Backed by years of verified data."
          cta={{ label: 'Compare professors →', href: '/search?q=' }}
          visual={
            <div style={{ display: 'flex', gap: 12 }}>
              {[MOCK_PROF_A, MOCK_PROF_B].map((p, i) => (
                <div key={i} style={{ flex: 1, background: '#F8FAFC', borderRadius: 14, padding: 16, border: `1px solid ${i === 0 ? 'rgba(var(--school-primary-rgb),0.3)' : 'rgba(13,27,42,0.07)'}` }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#0D1B2A', margin: '0 0 8px' }}>{p.name}</p>
                  <GpaBadge gpa={p.gpa} size="sm" />
                  <div style={{ marginTop: 10 }}>
                    <GradeBar grades={{ A: Math.round(p.gpa * 25), B: 22, C: 12, D: 4, F: 3, W: 2 }} height={8} />
                  </div>
                </div>
              ))}
            </div>
          }
        />

        <FeatureRow
          visualLeft
          eyebrow="Difficulty Intelligence"
          heading="Know what you're signing up for."
          body="Our 0–10 Difficulty Score combines withdrawal rates, GPA averages, and historical trends into one number."
          cta={{ label: 'See difficulty scores →', href: '/search?q=' }}
          visual={
            <div style={{ background: '#F8FAFC', borderRadius: 16, padding: 24, border: '1px solid rgba(13,27,42,0.07)', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-family-mono)', fontSize: 52, fontWeight: 900, color: '#EA580C', lineHeight: 1 }}>7.2</div>
              <div style={{ fontSize: 12, color: '#EA580C', fontWeight: 700, marginTop: 4, marginBottom: 16 }}>Hard</div>
              <div style={{ height: 10, borderRadius: 6, background: 'linear-gradient(90deg, #10B981 0%, #F59E0B 40%, #EA580C 70%, #DC2626 100%)', position: 'relative', marginBottom: 6 }}>
                <div style={{ position: 'absolute', left: '72%', top: '50%', transform: 'translate(-50%,-50%)', width: 14, height: 14, borderRadius: '50%', background: '#fff', border: '3px solid #EA580C', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94A3B8' }}>
                <span>Easy</span><span>Very Hard</span>
              </div>
            </div>
          }
        />
      </div>
    </section>
  )
}

function FeatureRow({
  visualLeft, eyebrow, heading, body, cta, visual,
}: {
  visualLeft: boolean
  eyebrow: string
  heading: string
  body: string
  cta: { label: string; href: string }
  visual: React.ReactNode
}) {
  const textCol = (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 8px' }}>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--school-primary)', marginBottom: 10 }}>{eyebrow}</p>
      <h3 style={{ fontSize: 26, fontWeight: 800, color: '#0D1B2A', lineHeight: 1.25, letterSpacing: '-0.02em', marginBottom: 14 }}>{heading}</h3>
      <p style={{ fontSize: 15, color: '#475569', lineHeight: 1.75, marginBottom: 20 }}>{body}</p>
      <a href={cta.href} style={{ fontSize: 14, fontWeight: 700, color: 'var(--school-primary)', textDecoration: 'none' }}>{cta.label}</a>
    </div>
  )
  const visualCol = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {visual}
    </div>
  )
  return (
    <div className="feature-row-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }}>
      {visualLeft ? <>{visualCol}{textCol}</> : <>{textCol}{visualCol}</>}
    </div>
  )
}

// ─── TRUST BAR ────────────────────────────────────────────────────────────────

function TrustBar({ totalRecords }: { totalRecords: number }) {
  const chips = [
    { icon: '📋', label: 'Official Records' },
    { icon: '🔒', label: 'Verified Data' },
    { icon: '🎓', label: '40,000+ Students' },
    { icon: '📊', label: `${Math.round(totalRecords / 1000)}k+ Grade Records` },
    { icon: '🏛️', label: 'FOIA Sourced' },
    { icon: '⚡', label: 'Updated Each Semester' },
  ]
  return (
    <section style={{ padding: '40px 0', background: '#fff', borderTop: '1px solid rgba(13,27,42,0.06)', borderBottom: '1px solid rgba(13,27,42,0.06)' }}>
      <div className="max-w-6xl mx-auto px-4" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
        {chips.map(({ icon, label }) => (
          <div key={label} style={{
            display: 'flex', alignItems: 'center', gap: 7,
            padding: '8px 16px', borderRadius: 999,
            border: '1px solid rgba(13,27,42,0.1)',
            fontSize: 13, color: '#475569', fontWeight: 500,
            background: '#fff',
          }}>
            <span>{icon}</span>
            <span>{label}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── TRENDING SECTION ────────────────────────────────────────────────────────

interface TrendingSectionProps {
  trending: SearchResult[] | undefined
  trendingPending: boolean
  trendingEmpty: boolean
  topCourses: TopResult[] | undefined
  topPending: boolean
}

function TrendingSection({ trending, trendingPending, trendingEmpty, topCourses, topPending }: TrendingSectionProps) {
  const display: Array<{ key: string; href: string; title: string; badges: SearchResult['badges'] }> =
    !trendingEmpty && trending && trending.length > 0
      ? trending.filter(r => r.type === 'course').slice(0, 6)
      : topCourses?.slice(0, 6).map(c => ({
          key: c.key, href: c.href, title: c.title, badges: c.badges,
        })) ?? []

  const trendingCourseIds = useMemo(
    () => display.filter(c => c.href.startsWith('/c/')).map(c => decodeURIComponent(c.href.slice(3))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [display.map(c => c.key).join(',')],
  )
  const { data: trendingSummaries } = useQuery({
    queryKey: ['course-enrichment-trending', trendingCourseIds],
    queryFn: () => fetchCourseEnrichments(trendingCourseIds),
    enabled: trendingCourseIds.length > 0,
    staleTime: 1000 * 60 * 5,
  })

  const isLoading = trendingPending || (trendingEmpty && topPending)

  return (
    <section style={{ padding: '64px 0', background: '#F8FAFC' }}>
      <div className="max-w-6xl mx-auto px-4">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
          <div style={{ borderLeft: '4px solid var(--school-primary)', paddingLeft: 16 }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0D1B2A', margin: 0, letterSpacing: '-0.02em' }}>
              📚 Courses Students Are Researching Right Now
            </h2>
            <p style={{ fontSize: 13, color: '#64748B', marginTop: 4 }}>Based on what UH students are looking up this semester — updated regularly</p>
          </div>
          <Link to="/top" style={{ fontSize: 13, color: 'var(--school-primary)', fontWeight: 600, textDecoration: 'none', flexShrink: 0, marginLeft: 16 }}>
            View all courses →
          </Link>
        </div>

        <div className="trending-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 16, padding: 16, border: '1px solid rgba(13,27,42,0.07)' }}>
                  <Skeleton height={14} width={80} style={{ marginBottom: 8 }} />
                  <Skeleton height={16} width="85%" style={{ marginBottom: 12 }} />
                  <Skeleton height={8} />
                </div>
              ))
            : display.map((item, idx) => {
                const [code, ...rest] = item.title.split(': ')
                const name = rest.join(': ')
                const gpa = extractGpa(item.badges)
                const dr = extractDropRate(item.badges)
                const courseId = decodeURIComponent(item.href.slice(3))
                const enrichment = trendingSummaries?.[courseId]
                const udiResult = enrichment
                  ? computeUDI(deriveUDIInputs({ avgGPA: enrichment.avgGPA, grades: enrichment.gradeCounts, sections: enrichment.sectionCount, schoolId: 'uh' }))
                  : undefined
                // Use enriched grade data when available, otherwise approximate for GradeBar display only
                const grades = enrichment?.gradeCounts ?? (() => {
                  const aFrac = gpa ? Math.max(0.05, (gpa - 2.0) / 2.5) : 0.3
                  return {
                    A: Math.round(aFrac * 100),
                    B: Math.round((0.95 - aFrac) * 60),
                    C: Math.round((0.95 - aFrac) * 25),
                    D: 5, F: 4,
                    W: Math.round(dr),
                  }
                })()
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    style={{
                      display: 'block', background: '#fff', borderRadius: 16, padding: '14px 16px',
                      border: '1px solid rgba(13,27,42,0.07)',
                      borderLeft: '4px solid var(--school-primary)',
                      boxShadow: '0 2px 8px rgba(13,27,42,0.05)',
                      textDecoration: 'none',
                      transition: 'box-shadow 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)'
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(13,27,42,0.10)'
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(13,27,42,0.05)'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--school-primary)', padding: '2px 8px', borderRadius: 4 }}>
                        {idx === 0 ? '🔥 ' : ''}{code}
                      </span>
                      <GpaBadge gpa={gpa} size="sm" />
                    </div>
                    <p style={{ fontSize: 12.5, fontWeight: 600, color: '#0D1B2A', margin: '0 0 10px', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {name || code}
                    </p>
                    {udiResult && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <UDIBadge udi={udiResult.udi} />
                      </div>
                    )}
                    <GradeBar grades={grades} height={6} />
                  </a>
                )
              })
          }
        </div>
      </div>
    </section>
  )
}

// ─── SCHOOL COVERAGE SECTION ─────────────────────────────────────────────────

interface SchoolCoverageProps {
  stats: { totalRecords: number; totalCourses: number; totalProfessors: number; schools: number } | undefined
}

const EXTENDED_SCHOOLS = [
  ...SCHOOLS_REGISTRY,
  { id: 'gatech', shortName: 'GT', name: 'Georgia Tech', color: '#B3A369', location: 'Atlanta, GA', isActive: false, addedAt: '2024-01-01' },
  { id: 'uf', shortName: 'UF', name: 'University of Florida', color: '#FA4616', location: 'Gainesville, FL', isActive: false, addedAt: '2024-01-01' },
]

function SchoolCoverageSection({ stats }: SchoolCoverageProps) {
  return (
    <section style={{ padding: '64px 0 80px', background: '#fff', borderTop: '1px solid rgba(13,27,42,0.06)' }}>
      <div className="max-w-6xl mx-auto px-4">
        <div style={{ marginBottom: 36, borderLeft: '4px solid var(--school-primary)', paddingLeft: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0D1B2A', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
            Starting with UH. Expanding fast.
          </h2>
          <p style={{ fontSize: 14, color: '#64748B' }}>Built multi-university from day one.</p>
        </div>

        <div className="school-coverage-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 28 }}>
          {EXTENDED_SCHOOLS.map(school => (
            <div
              key={school.id}
              style={{
                borderRadius: 16,
                border: school.isActive
                  ? `2px solid ${school.color}`
                  : '1px solid rgba(13,27,42,0.07)',
                background: school.isActive ? `${school.color}08` : '#FAFAFA',
                padding: '18px 16px',
                opacity: school.isActive ? 1 : 0.6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: school.isActive ? 10 : 0 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: school.color, flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#0D1B2A', margin: 0 }}>{school.shortName}</p>
                  <p style={{ fontSize: 11, color: '#94A3B8', margin: 0 }}>{school.location}</p>
                </div>
                <span style={{
                  marginLeft: 'auto', fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.07em', textTransform: 'uppercase',
                  padding: '2px 8px', borderRadius: 10,
                  color: school.isActive ? '#059669' : '#94A3B8',
                  background: school.isActive ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)',
                }}>
                  {school.isActive ? 'Live' : 'Soon'}
                </span>
              </div>
              {school.isActive && stats && (
                <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 4px', paddingLeft: 20 }}>
                  {Math.round(stats.totalRecords / 1000)}k records · {Math.round(stats.totalCourses / 1000).toFixed(1)}k courses · {Math.round(stats.totalProfessors / 1000).toFixed(1)}k professors
                </p>
              )}
              {!school.isActive && (
                <div style={{ paddingLeft: 20, marginTop: 6 }}>
                  <a
                    href={`mailto:hello@unilytics.app?subject=Notify me when ${school.name} launches`}
                    style={{ fontSize: 11, color: 'var(--school-primary)', fontWeight: 600, textDecoration: 'none' }}
                  >
                    Notify me →
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center' }}>
          <a
            href="mailto:hello@unilytics.app?subject=Suggest my university"
            style={{
              display: 'inline-block', padding: '10px 24px',
              borderRadius: 12, fontSize: 14, fontWeight: 700,
              background: 'var(--school-primary)', color: '#fff', textDecoration: 'none',
            }}
          >
            Suggest your university →
          </a>
        </div>
      </div>
    </section>
  )
}
